/**
 * The picture area of the gomme magique : the whole photo (never the crop of the page) with the
 * brush over it, then the result over the photo, cut by a slider to compare before and after.
 *
 *   load(url)        → Promise<img>   the photo to brush over, once loaded
 *   showResult(url, { compare })  → Promise   the erased version, compared at 50 % with the
 *                                     slider, or shown alone over the photo (compare : false)
 *   hideResult()                      back to the photo alone
 *   busy(val)                         a loader over the photo only, the rest of the screen usable
 *   erasing(overlay, bounds, points)  the erase running : the photo dimmed, the area brushed
 *                                     (`overlay`, the brush's own picture of it, used as a CSS mask)
 *                                     shimmering, stars twinkling at `points` inside it (0..1 of
 *                                     the photo, from Brush.samplePoints), a progress bar under it
 *   stopErasing()                     the animation gone, a cancel or a failure
 *   place(rect, sliderTop)            the photo at this rect of the view ({ left, top, width,
 *                                     height }) rather than fitted in it, the slider at sliderTop
 *                                     rather than under the photo : null to fit again
 */
fx.ns("shared.magiceraser", class EraserView extends fx.Component {

    get image()
    {
        return this.find(".before");
    }

    get result()
    {
        return this.find(".after");
    }

    busy(val)
    {
        this.toggleClass("busy", !!val);
        this._placeBusy();
    }

    place(rect, sliderTop)
    {
        this._rect = rect || null;
        this._sliderTop = rect && sliderTop !== undefined ? sliderTop : null;
        this._fit();

        if (this.hasClass("comparing"))
        {
            this._placeResult();
            this.compare(this.find(".compare").value);
        }
    }

    load(url)
    {
        return this._loadInto(this.image, url).then((image) => {
            this._fit();
            return image;
        });
    }

    /**
     * options.reveal ({ x, y }, 0..1 of the photo) : the result grows in a circle from there, once
     * the progress bar of erasing() has filled up
     */
    showResult(url, options)
    {
        let compare = !options || options.compare !== false;
        let reveal = options && options.reveal;

        return this._loadInto(this.result, url)
            .then(() => this.hasClass("erasing") ? this._finishProgress() : null)
            .then(() => {
                this.stopErasing();
                this.addClass("comparing");
                this.toggleClass("resultOnly", !compare);
                this._placeResult();

                if (compare)
                    this.compare(50);
                else
                    this.result.style.clipPath = "";

                if (reveal)
                {
                    this.result.style.setProperty("--cx", (reveal.x * 100) + "%");
                    this.result.style.setProperty("--cy", (reveal.y * 100) + "%");
                    this.result.classList.remove("reveal");
                    void this.result.offsetWidth;   // restart the animation of a second try
                    this.result.classList.add("reveal");
                }
            });
    }

    hideResult()
    {
        this.removeClass("comparing");
        this.removeClass("resultOnly");
        this.result.classList.remove("reveal");
        this.result.removeAttribute("src");
    }

    erasing(overlay, bounds, points)
    {
        let shimmer = this.find(".shimmer");
        let box = bounds || { x0 : 0.3, y0 : 0.3, x1 : 0.7, y1 : 0.7 };

        shimmer.style.webkitMaskImage = shimmer.style.maskImage = overlay ? "url(" + overlay + ")" : "";

        // the stars, as many as the area is large (on screen), each moving to another point of the
        // area every time it has twinkled
        this._points = points && points.length ? points : [[(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2]];

        let image = this.image;
        let area = (box.x1 - box.x0) * image.offsetWidth * (box.y1 - box.y0) * image.offsetHeight;
        let count = Math.max(8, Math.min(40, Math.round(area / 1200)));
        let stars = "";

        for (let i = 0; i < count; i++)
        {
            let [x, y] = this._points[i % this._points.length];
            let size = 10 + Math.round(Math.random() * 14);

            stars += '<i class="star" style="left:' + (x * 100) + '%;top:' + (y * 100) + '%;width:' + size + 'px;height:' + size + 'px;' +
                     'animation-duration:' + (0.9 + Math.random() * 1.1).toFixed(2) + 's;animation-delay:' + (Math.random() * 1.2).toFixed(2) + 's"><b></b></i>';
        }

        this.find(".sparkles").innerHTML = stars;

        this._progress = 0;
        this._progressStart = Date.now();
        this._setProgress(0, false);
        clearInterval(this._progressTimer);
        this._progressTimer = setInterval(() => this._tickProgress(), 200);

        this.addClass("erasing");
        this._placeErasing();
    }

    stopErasing()
    {
        clearInterval(this._progressTimer);
        this._progressTimer = null;
        this.removeClass("erasing");

        let sparkles = this.find(".sparkles");
        if (sparkles)
            sparkles.innerHTML = "";
    }

    destroy()
    {
        clearInterval(this._progressTimer);

        super.destroy();
    }

    // pct of the width showing the photo as it was, from the left
    compare(pct)
    {
        this.find(".compare").value = pct;
        this.result.style.clipPath = "inset(0 0 0 " + pct + "%)";
        this.find(".divider").style.left = (this.image.offsetLeft + this.image.offsetWidth * pct / 100) + "px";
    }

    onReady()
    {
        super.onReady();

        this.on("input", this._onCompare, this.find(".compare"));
        this.on("animationiteration", this._onTwinkled, this.find(".sparkles"));
    }

    onDisplayUpdated()
    {
        super.onDisplayUpdated();

        this._fit();

        this._placeErasing();

        if (this.hasClass("comparing"))
        {
            this._placeResult();

            if (!this.hasClass("resultOnly"))
                this.compare(this.find(".compare").value);
        }
    }

    render()
    {
        return  '<div class="eraserFrame">' +
                    '<img class="before" draggable="false" alt="">' +
                    '<img class="after" draggable="false" alt="">' +
                    '<div class="divider"></div>' +
                    '<span class="label label-before">' + fx.text("Avant") + '</span>' +
                    '<span class="label label-after">' + fx.text("Après") + '</span>' +
                    '<div class="dim"></div>' +
                    '<div class="shimmer"></div>' +
                    '<div class="sparkles"></div>' +
                    '<div class="progress"><div class="bar"></div></div>' +
                    '<div class="loader"><div class="spinner"></div></div>' +
                '</div>' +
                '<input class="compare" type="range" min="0" max="100" value="50">';
    }

    // ---- privates

    // a star out : it comes back somewhere else in the area
    _onTwinkled(e)
    {
        let star = e.target;
        if (!star.classList.contains("star") || !this._points)
            return;

        let [x, y] = this._points[Math.floor(Math.random() * this._points.length)];
        star.style.left = (x * 100) + "%";
        star.style.top = (y * 100) + "%";
    }

    _onCompare(e)
    {
        this.compare(e.target.value);
    }

    // the whole photo, as large as the area allows (CSS max-height does not hold in the fx layout).
    // The brush follows the picture's size through its ResizeObserver.
    _fit()
    {
        let image = this.image;
        if (!this.node || !image || !image.naturalWidth)
            return;

        this.delay(this._placeBusy, 0);
        this.delay(this._placeErasing, 0);

        // at a rect given by the caller (EditPhoto, where the crop showed it)
        if (this._rect)
        {
            Object.assign(image.style, {
                position : "absolute",
                left : Math.round(this._rect.left) + "px",
                top : Math.round(this._rect.top) + "px",
                width : Math.round(this._rect.width) + "px",
                height : Math.round(this._rect.height) + "px",
                margin : "0",
            });
            return;
        }

        Object.assign(image.style, { position : "", left : "", top : "" });

        let width = this.node.offsetWidth;
        let height = this.node.offsetHeight - this.SLIDER_ROOM;
        if (width <= 0 || height <= 0)
            return;

        let scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
        let fitWidth = Math.floor(image.naturalWidth * scale);
        let fitHeight = Math.floor(image.naturalHeight * scale);

        Object.assign(image.style, {
            width : fitWidth + "px",
            height : fitHeight + "px",
            marginLeft : Math.floor((width - fitWidth) / 2) + "px",
            marginTop : Math.floor((height - fitHeight) / 2) + "px",
        });
    }

    get SLIDER_ROOM()
    {
        return 34;
    }

    // the loader covers the part of the photo on show : the photo may run past the view (the crop
    // of a page), the spinner must not sit in what is clipped
    _placeBusy()
    {
        let image = this.image;
        let loader = this.find(".loader");
        let frame = this.find(".eraserFrame");
        if (!image || !loader || !frame)
            return;

        let left = Math.max(0, image.offsetLeft);
        let top = Math.max(0, image.offsetTop);
        let right = Math.min(frame.clientWidth, image.offsetLeft + image.offsetWidth);
        let bottom = Math.min(frame.clientHeight, image.offsetTop + image.offsetHeight);

        Object.assign(loader.style, {
            left : left + "px",
            top : top + "px",
            width : Math.max(0, right - left) + "px",
            height : Math.max(0, bottom - top) + "px",
        });
    }

    // what the customer waits for is unknown : the bar runs fast then slows down, never full until
    // the result is there (95 % of the way after ~18 s)
    get PROGRESS_PACE()
    {
        return 6000;
    }

    _tickProgress()
    {
        let t = Date.now() - this._progressStart;

        this._setProgress(0.95 * (1 - Math.exp(-t / this.PROGRESS_PACE)), true);
    }

    _setProgress(value, animate)
    {
        let bar = this.find(".progress .bar");
        if (!bar)
            return;

        bar.style.transition = animate ? "width 0.2s linear" : "none";
        bar.style.width = (value * 100) + "%";
    }

    // the bar filled up before the result shows
    _finishProgress()
    {
        clearInterval(this._progressTimer);
        this._progressTimer = null;

        let bar = this.find(".progress .bar");
        if (bar)
        {
            bar.style.transition = "width 0.3s cubic-bezier(.3, .1, .4, 1)";
            bar.style.width = "100%";
        }

        return new Promise((resolve) => setTimeout(resolve, 350));
    }

    // the dim and the shimmer lie on the whole photo (the mask is the whole photo's), the progress
    // bar under the part of it on show
    _placeErasing()
    {
        let image = this.image;
        let frame = this.find(".eraserFrame");
        if (!image || !frame || !this.hasClass("erasing"))
            return;

        let rect = {
            left : image.offsetLeft + "px",
            top : image.offsetTop + "px",
            width : image.offsetWidth + "px",
            height : image.offsetHeight + "px",
        };

        Object.assign(this.find(".dim").style, rect);
        Object.assign(this.find(".shimmer").style, rect);
        Object.assign(this.find(".sparkles").style, rect);

        let left = Math.max(0, image.offsetLeft);
        let right = Math.min(frame.clientWidth, image.offsetLeft + image.offsetWidth);
        let bottom = Math.min(frame.clientHeight, image.offsetTop + image.offsetHeight);
        // 240 px, 28 px off the bottom ; 180 and 18 on a phone
        let small = right - left < 500;
        let width = Math.min(small ? 180 : 240, Math.max(0, right - left - 40));

        Object.assign(this.find(".progress").style, {
            left : Math.round(left + (right - left - width) / 2) + "px",
            top : Math.round(bottom - (small ? 18 : 28) - 6) + "px",
            width : width + "px",
        });
    }

    // the result sits exactly on the photo, whatever room the frame gives it
    _placeResult()
    {
        let image = this.image;
        let result = this.result;
        let labels = this.find(".label");

        Object.assign(result.style, {
            left : image.offsetLeft + "px",
            top : image.offsetTop + "px",
            width : image.offsetWidth + "px",
            height : image.offsetHeight + "px",
        });

        // the slider right under the photo, not at the bottom of a tall phone screen
        this.find(".compare").style.top = (this._sliderTop !== null && this._sliderTop !== undefined ? this._sliderTop : image.offsetTop + image.offsetHeight + 8) + "px";

        this.find(".divider").style.top = image.offsetTop + "px";
        this.find(".divider").style.height = image.offsetHeight + "px";

        if (labels instanceof Array)
            labels.forEach((label) => label.style.top = (image.offsetTop + 8) + "px");
    }

    _loadInto(img, url)
    {
        return new Promise((resolve, reject) => {
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("image " + url));
            img.src = url;
        });
    }
});
